/* eslint-disable import/prefer-default-export */
import { createSelector } from 'reselect';
import { ListingState } from './types';
import { AppState } from 'shared/store/app/types';
import { objectValues } from 'shared/helpers/utils/objects';
import { IProduct } from 'components/AdPlatformWidgets/types';
import { DEFAULT_STORE } from 'shared/helpers/storeUrl';

export const listing = (state: { listing: ListingState }): ListingState => state.listing;
const app = (state: { app?: AppState }) => state.app;

export const isFetching = createSelector([listing], (listingData) => listingData.isFetching);

export const isSecondaryFetching = createSelector(
  [listing],
  (listingData) => listingData.isSecondaryFetching
);

export const isFetchingError = createSelector(
  [listing],
  (listingData) => listingData.isFetchingError
);

export const isNotFound = createSelector([listing], (listingData) => listingData.isNotFound);

export const getProducts = createSelector([listing], (listingData) => listingData.products);

export const getCumulativeOOSCount = createSelector(
  [listing],
  (listingData) => listingData.cumulativeOOSCount || 0
);

export const getFirstTwelveProducts = createSelector([listing], (listingData) => {
  return (
    objectValues(listingData.products)[0] &&
    objectValues(listingData.products)[0]
      .filter((item: IProduct) => {
        return item.type !== 'tiptile';
      })
      .slice(0, 12)
  );
});

export const getTotalCount = createSelector([listing], (listingData) => listingData.count);

export const getFilters = createSelector([listing], (listingData) => listingData.filters);

export const getBannerImages = createSelector(
  [listing],
  (listingData) => listingData.banner_images
);

export const getTitle = createSelector([listing], (listingData) => listingData.title);

export const getIsBrand = createSelector([listing], (listingData) => listingData.isBrand);

export const getAppliedFilters = createSelector(
  [listing],
  (listingData) => listingData.appliedFilters
);

export const getCategoryId = createSelector([listing], (listingData) => listingData.categoryId);

export const getPage = createSelector([listing], (listingData) => listingData.currentPage);

export const getMetaData = createSelector([listing], (listingData) => listingData.meta_data);

export const getErrorId = createSelector([listing], (listingData) => listingData.errorId);

export const getErrorStatusCode = createSelector(
  [listing],
  (listingData) => listingData.errorStatusCode || ''
);

export const getTopCollection = createSelector(
  [listing],
  (listingData) => listingData.topCollection
);

export const getSearchTerm = createSelector([listing], (listingData) => listingData.searchTerm);

export const getImpressiveProducts = createSelector([listing], (listingData) => {
  return (
    objectValues(listingData.products)[0] &&
    objectValues(listingData.products)[0]
      .map((item: IProduct) => {
        return item.id;
      })
      .join(',')
  );
});

export const getUIMessage = createSelector([listing], (listingData) => listingData.ui_message);

export const getQueryExpansionTerm = createSelector(
  [listing],
  (listingData) => listingData.query_expansion_term
);

export const getQueryExpansionType = createSelector(
  [listing],
  (listingData) => listingData.query_expansion_type
);

export const getFooterHTML = createSelector([listing], (listingData) => listingData.footerHTML);

export const getFaq = createSelector([listing], (listingData) => listingData.faq);
export const getTopBestsellers = createSelector(
  [listing],
  (listingData) => listingData.topBestsellers || []
);

export const getFilterGuides = createSelector([listing], (listingData) => listingData.filterGuides);

export const getCategoryUrl = createSelector([listing], (listingData) => listingData.categoryURL);

export const getAdPlatformWidgets = createSelector(
  [listing],
  (listingData) => listingData.adPlatformData
);

export const getAdplatformWidgets = createSelector(
  [listing],
  (listingData) => listingData.adplatformWidgets
);

export const getCrossPlatformRedirectConfig = createSelector(
  [listing],
  (listingData) => listingData.crossPlatformRedirectConfig
);

export const getKidsFilters = createSelector([listing], (listingData) => listingData.kids_filters);

export const getEntityData = createSelector([listing], (listingData) => listingData.entityData);

export const getTrackingMetadata = createSelector(
  [listing, app],
  (listingData, appData) => {
    const trackingMetaData = listingData?.trackingMetaData || {};
    const storeId = appData?.storeId;
    const storeSelected = storeId === DEFAULT_STORE ? 'fashion' : storeId;

    return storeSelected
      ? { ...trackingMetaData, store_selected: storeSelected }
      : trackingMetaData;
  }
);

export const getMerchandisingDetails = createSelector(
  [listing],
  (listingData) => listingData?.trackingMetaData?.merchandising_details || {}
);

export const getInternationalURLs = createSelector(
  [listing],
  (listingData) => listingData.intl_url
);

export const getBestPrice = createSelector([listing], (listingData) => listingData.bestPrice);

export const getFomoTag = createSelector([listing], (listingData) => listingData.fomoTag);

export const getQuickFilters = createSelector(
  [listing],
  (listingData) => listingData.quickFilters || []
);

export const getBreadCrumbs = createSelector(
  [listing],
  (listingData) => listingData.breadcrumbs || []
);

export const getMetaTitle = createSelector(
  [listing],
  (listingData) => listingData?.meta_data?.h1_tag || ''
);

export const getBrandLaunchData = createSelector(
  [listing],
  (listingData) => ({
    isLaunchingSoon: listingData.brand_launching_soon || false,
    metadata: listingData.brand_launch_metadata || null
  })
);

export const getOnSaleListing = createSelector([listing], (listingData) => listingData.onSaleData);

export const getSpecialTagList = createSelector([listing], (listingData) => listingData.specialTagData);

export const getRelatedSearches = createSelector(
  [listing],
  (listingData) => listingData.relatedSearches
);
export const getStoreRedirect = createSelector([listing], (listingData) => listingData.store_redirection);

export const getStoreRedirectCount = createSelector([listing], (listingData) => listingData.storeRedirectCount || 0);